<template>
  <div class="card mb-3 stage-card">
    <div class="card-body">
      <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h5 class="card-title m-0">Calendario de etapas</h5>
        <span class="text-muted small">Entrega y publicación</span>
      </div>

      <div class="stage-carousel" v-if="stageCalendars.length">
        <button class="nav-arrow left" type="button" @click="scrollStages('left')">
          <i class="fa-solid fa-chevron-left"></i>
        </button>
        <div class="stage-cards" ref="stageList">
          <button
              v-for="stage in stageCalendars"
              :key="stage.id"
              class="stage-pill-card"
              :class="{ active: selectedStageId === stage.id }"
              type="button"
              @click="selectStage(stage.id)"
          >
            <div class="stage-pill-title">{{ stageLabel(stage.stage_code) }}</div>
            <div class="stage-pill-row" @click.stop="selectStageDetail(stage.id, 'open')">
              <i class="fa-solid fa-door-open text-primary"></i>
              <div>
                <div class="row-label text-muted">{{ labelOpen }}</div>
                <div class="row-value">{{ formatDateTime(stage.upload_opens_at) }}</div>
              </div>
            </div>
            <div class="stage-pill-row" @click.stop="selectStageDetail(stage.id, 'close')">
              <i class="fa-solid fa-door-closed text-danger"></i>
              <div>
                <div class="row-label text-muted">{{ labelClose }}</div>
                <div class="row-value">{{ formatDateTime(stage.upload_closes_at) }}</div>
              </div>
            </div>
            <div class="stage-pill-row" @click.stop="selectStageDetail(stage.id, 'publish')">
              <i class="fa-solid fa-bullhorn text-success"></i>
              <div>
                <div class="row-label text-muted">{{ labelPublish }}</div>
                <div class="row-value">{{ formatDateTime(stage.publish_at) }}</div>
              </div>
            </div>
          </button>
        </div>
        <button class="nav-arrow right" type="button" @click="scrollStages('right')">
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>

      <div v-if="activeStage && detailSelection" class="detail-card mt-3">
        <h6 class="mb-1">{{ detailTitle }}</h6>
        <p class="mb-1 fw-semibold">{{ detailDate }}</p>
        <p class="mb-0 text-muted">{{ detailDescription }}</p>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: 'CompetitionStageCalendar',
  props: {
    competition: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedStageId: null,
      detailSelection: null,
      debugCalendar: true,
      calendarsLocal: []
    };
  },
  computed: {
    stageCalendars() {
      return this.calendarsLocal;
    },
    labelOpen() {
      return this.$t ? this.$t('competitions.stage_calendar.open') : 'Entrega abre';
    },
    labelClose() {
      return this.$t ? this.$t('competitions.stage_calendar.close') : 'Entrega cierra';
    },
    labelPublish() {
      return this.$t ? this.$t('competitions.stage_calendar.publish') : 'Publicación';
    },
    infoOpen() {
      return this.$t ? this.$t('competitions.stage_calendar.open_info') : 'Desde este momento puedes subir tu video para la etapa.';
    },
    infoClose() {
      return this.$t ? this.$t('competitions.stage_calendar.close_info') : 'Después de este horario no se aceptarán más envíos.';
    },
    infoPublish() {
      return this.$t ? this.$t('competitions.stage_calendar.publish_info') : 'Los jueces califican entre el cierre y esta fecha; aquí se publican resultados.';
    },
    activeStage() {
      if (!this.stageCalendars.length || !this.selectedStageId) return null;
      return this.stageCalendars.find(s => s.id === this.selectedStageId) || null;
    },
    detailTitle() {
      const map = {
        open: this.labelOpen,
        close: this.labelClose,
        publish: this.labelPublish
      };
      return map[this.detailSelection] || '';
    },
    detailDescription() {
      const map = {
        open: this.infoOpen,
        close: this.infoClose,
        publish: this.infoPublish
      };
      return map[this.detailSelection] || '';
    },
    detailDate() {
      if (!this.activeStage || !this.detailSelection) return '';
      const map = {
        open: this.activeStage.upload_opens_at,
        close: this.activeStage.upload_closes_at,
        publish: this.activeStage.publish_at
      };
      return this.formatDateTime(map[this.detailSelection]);
    }
  },
  watch: {
    competition: {
      handler(val) {
        const payload = val?.competition_stage_calendars
          ?? val?.stage_calendars
          ?? val?.stageCalendars
          ?? [];
        this.mergeCalendars(payload);
      },
      deep: false,
      immediate: true
    },
    stageCalendars(list) {
      if (!Array.isArray(list) || !list.length) {
        this.selectedStageId = null;
        this.detailSelection = null;
      } else if (!this.selectedStageId) {
        const preferredStageId = this.resolvePreferredStageId(list);
        this.selectedStageId = preferredStageId;
      }
    }
  },
  methods: {
    normalizeCalendars(payload) {
      if (Array.isArray(payload)) return payload;
      if (payload && typeof payload === 'object') return [payload];
      return [];
    },
    mergeCalendars(payload) {
      const incoming = this.normalizeCalendars(payload);
     
      // Reemplaza completamente el array de calendarios con lo que llega del broadcast
      const sorted = incoming
        .filter(item => item && item.id !== undefined && item.id !== null)
        .sort((a, b) => {
          const orderA = Number(a.order) || 0;
          const orderB = Number(b.order) || 0;
          if (orderA !== orderB) return orderA - orderB;
          const idA = this.normalizeId(a.id) || 0;
          const idB = this.normalizeId(b.id) || 0;
          return idA - idB;
        });

      this.calendarsLocal = sorted;

      if (!sorted.length) {
        this.selectedStageId = null;
        this.detailSelection = null;
        return;
      }

      const preferredStageId = this.resolvePreferredStageId(sorted);
      const hasSelected = sorted.some(s => this.normalizeId(s.id) === this.normalizeId(this.selectedStageId));
      const selectedStage = hasSelected
        ? sorted.find(s => this.normalizeId(s.id) === this.normalizeId(this.selectedStageId))
        : null;

      if (!hasSelected) {
        this.selectedStageId = preferredStageId;
        this.detailSelection = null;
        return;
      }

      if (selectedStage && !this.isStageActive(selectedStage)) {
        const preferredChanged = this.normalizeId(preferredStageId) !== this.normalizeId(this.selectedStageId);
        if (preferredChanged) {
          this.selectedStageId = preferredStageId;
          this.detailSelection = null;
        }
      }
    },
    resolvePreferredStageId(list = []) {
      if (!Array.isArray(list) || !list.length) {
        return null;
      }

      const activeStages = list.filter(stage => this.isStageActive(stage));
      if (activeStages.length) {
        return activeStages[activeStages.length - 1].id;
      }

      return list[0].id;
    },
    parseStageDate(raw) {
      if (!raw) return null;

      const normalized = typeof raw === 'string' && raw.includes(' ') ? raw.replace(' ', 'T') : raw;
      const date = new Date(normalized);
      return Number.isNaN(date.getTime()) ? null : date;
    },
    isStageActive(stage) {
      if (!stage) return false;

      const now = new Date();
      const opensAt = this.parseStageDate(stage.upload_opens_at);
      const closesAt = this.parseStageDate(stage.upload_closes_at);
      const publishAt = this.parseStageDate(stage.publish_at);
      const startAt = opensAt || closesAt || publishAt;
      const endAt = publishAt || closesAt;

      if (!startAt && !endAt) {
        return false;
      }

      if (startAt && now < startAt) {
        return false;
      }

      if (endAt && now > endAt) {
        return false;
      }

      return true;
    },
    normalizeId(raw) {
      if (raw === null || raw === undefined) return null;
      const num = Number(raw);
      if (!Number.isNaN(num)) return num;
      return String(raw);
    },
    selectStage(id) {
      if (this.selectedStageId === id) {
        // Re-click closes any open detail
        this.detailSelection = null;
        return;
      }
      this.selectedStageId = id;
      this.detailSelection = null;
     
    },
    selectStageDetail(id, kind) {
      this.selectStage(id);
      this.detailSelection = this.detailSelection === kind ? null : kind;
    },
    scrollStages(direction) {
      const el = this.$refs.stageList;
      if (!el) return;
      const delta = 240;
      el.scrollBy({ left: direction === 'left' ? -delta : delta, behavior: 'smooth' });
    },
    stageLabel(code) {
      if (!code) return '—';
      const str = String(code);
      // qualification_1 -> rounds.qualifying + número
      const qualMatch = str.match(/^qualification_(\d+)$/);
      if (qualMatch) {
        const num = qualMatch[1];
        const base = this.$t ? this.$t('rounds.qualifying') : 'Calificación';
        return `${base} ${num}`;
      }

      // stage1, stage2, etc. -> rounds.stage1, rounds.stage2
      const stageMatch = str.match(/^stage(\d+)$/);
      if (stageMatch) {
        const key = `rounds.stage${stageMatch[1]}`;
        if (this.$te && this.$te(key)) return this.$t(key);
        if (this.$t) return this.$t('rounds.stage1');
        return `Stage ${stageMatch[1]}`;
      }

      // Fallback a otras traducciones conocidas
      const map = {
        round_16: this.$t ? this.$t('rounds.stage5') : 'Dieciseisavos de final',
        round_8: this.$t ? this.$t('rounds.stage4') : 'Octavos de final',
        round_4: this.$t ? this.$t('rounds.stage2') : 'Semifinal',
        final: this.$t ? this.$t('rounds.stage1') : 'Final'
      };
      return map[str] || str;
    },
    formatDateTime(raw) {
      if (!raw) return '—';
      const normalized = typeof raw === 'string' && raw.includes(' ') ? raw.replace(' ', 'T') : raw;
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) return raw;
      const dateStr = date.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
      const timeStr = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      return `${dateStr} · ${timeStr}`;
    },
    formatDate(raw) {
      if (!raw) return '—';
      const normalized = typeof raw === 'string' && raw.includes(' ') ? raw.replace(' ', 'T') : raw;
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) return raw;
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
    },
    formatTime(raw) {
      if (!raw) return '';
      const normalized = typeof raw === 'string' && raw.includes(' ') ? raw.replace(' ', 'T') : raw;
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }
  }
};
</script>

<style scoped>
.stage-card {
  background: #f9fbff;
  border: 1px solid #e3ecff;
}
.stage-carousel {
  position: relative;
  margin-bottom: 12px;
  padding: 0 52px 8px;
}
.stage-cards {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 4px 8px 12px;
  scroll-padding-left: 32px;
  scroll-padding-right: 32px;
  scroll-snap-type: x mandatory;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}
.stage-cards::-webkit-scrollbar { /* Chrome/Safari */
  display: none;
}
.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: 1px solid #d6dbe5;
  background: #fff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  z-index: 2;
}
.nav-arrow.left { left: 8px; }
.nav-arrow.right { right: 8px; }

.stage-pill-card {
  scroll-snap-align: start;
  flex: 0 0 calc((100% - 32px) / 3);
  min-width: 200px;
  max-width: 260px;
  border: 1px solid #e6e9ef;
  border-radius: 12px;
  background: #fff;
  padding: 12px;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.05);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease;
}

.stage-pill-card.active {
  border-color: #0d6efd;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.12);
  transform: translateY(-1px);
}

.stage-pill-card.inactive {
  opacity: 0.55;
  cursor: not-allowed;
}

.stage-pill-title {
  font-weight: 700;
  margin-bottom: 8px;
  font-size: 14px;
  color: #0d1b2a;
}

.stage-pill-row {
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: 8px;
  align-items: start;
  padding: 6px 0;
  border-top: 1px solid #f1f3f7;
}

.stage-pill-row:first-of-type {
  border-top: none;
  padding-top: 0;
}

.row-label {
  font-size: 12px;
  line-height: 1.2;
}

.row-value {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.stage-detail {
  background: #fff;
  border: 1px solid #e6e9ef;
  border-radius: 10px;
  padding: 16px;
}

.timeline {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 18px;
  align-items: start;
}

.timeline-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e6e9ef;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
}

.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-top: 4px;
}
.timeline-dot.upload { background: #0d6efd; }
.timeline-dot.close { background: #dc3545; }
.timeline-dot.publish { background: #198754; }

.timeline-item.active {
  border-color: #0d6efd;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.12);
}

.timeline-item .text-muted.small {
  font-size: 0.8rem;
}

.detail-card {
  margin-top: 16px;
  padding: 14px;
  border: 1px solid #e6e9ef;
  border-radius: 10px;
  background: #f8fafc;
}

@media (max-width: 576px) {
  .stage-cards {
    gap: 10px;
    padding: 4px 10px 10px;
    scroll-padding-left: 10px;
    scroll-padding-right: 10px;
  }
  .stage-pill-card {
    flex: 0 0 100%;
    min-width: 0;
    max-width: 100%;
  }
  .timeline {
    grid-template-columns: 1fr;
  }
  .timeline-item {
    min-width: 0;
  }
}
</style>
